import { useState, useEffect } from "react";
import { 
  Search, Filter, Grid3X3, List, FileText, Image, Video, Mail, 
  File, Code, Archive, Download, Eye, MoreHorizontal, Clock,
  ExternalLink, MessageSquare, Sparkles, RefreshCw
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { searchService, SearchResult } from "../../services/searchService";
import { EmailData } from "../../types";
import { documentService } from "../../services/documentService";

interface DocumentsPageProps {
  mcpServices?: Record<string, boolean>;
  onNavigateToSettings?: () => void;
}

// Use SearchResult from searchService instead of local interface
type Document = SearchResult;

// Sample documents to show when no real documents are available
const getSampleDocuments = (): Document[] => [
  {
    id: "sample-1",
    title: "Welcome to Document Search",
    description: "This is a sample document to show how the interface works. Connect your Google Drive and Gmail to see real documents here.",
    type: "document",
    source: "Local",
    size: "2.4 KB",
    modified: "Just now",
    thumbnail: "📋",
    metadata: { isSample: true }
  },
  {
    id: "sample-2", 
    title: "How to Connect Services",
    description: "Go to Settings to connect your Google Drive, Gmail, and other services to search your real documents.",
    type: "document",
    source: "Local",
    size: "1.8 KB", 
    modified: "Just now",
    thumbnail: "⚙️",
    metadata: { isSample: true }
  },
  {
    id: "sample-3",
    title: "Document Types Supported",
    description: "You can search through Google Docs, Sheets, PDFs, emails, images, and more once connected.",
    type: "document",
    source: "Local",
    size: "3.1 KB",
    modified: "Just now", 
    thumbnail: "📁",
    metadata: { isSample: true }
  }
];

export function DocumentsPage({ mcpServices = {}, onNavigateToSettings }: DocumentsPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeFilter, setActiveFilter] = useState("all");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [searchResults, setSearchResults] = useState<any>(null);

  const filterOptions = [
    { id: "all", label: "All Files", count: documents.length },
    { id: "recent", label: "Recent", count: searchResults?.sources?.recent || 0 },
    { id: "documents", label: "Documents", count: documents.filter(d => d.type === "document").length },
    { id: "images", label: "Images", count: documents.filter(d => d.type === "image").length },
    { id: "videos", label: "Videos", count: documents.filter(d => d.type === "video").length },
    { id: "emails", label: "Emails", count: searchResults?.sources?.email || 0 },
    { id: "memories", label: "Memories", count: searchResults?.sources?.memory || 0 }
  ];

  const filteredDocuments = documents.filter(doc => {
    const matchesFilter = activeFilter === "all" ||
                         (activeFilter === "recent" && doc.source === "Drive") ||
                         (activeFilter === "documents" && doc.type === "document") ||
                         (activeFilter === "images" && doc.type === "image") ||
                         (activeFilter === "videos" && doc.type === "video") ||
                         (activeFilter === "emails" && doc.type === "email") ||
                         (activeFilter === "memories" && doc.type === "memory");
    
    return matchesFilter;
  });

  const getIcon = (type: string) => {
    const iconProps = { size: 20, className: "text-white" };
    switch (type) {
      case "image": return <Image {...iconProps} />;
      case "video": return <Video {...iconProps} />;
      case "document": return <FileText {...iconProps} />;
      case "code": return <Code {...iconProps} />;
      case "archive": return <Archive {...iconProps} />;
      case "email": return <Mail {...iconProps} />;
      default: return <File {...iconProps} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "image": return "bg-purple-500";
      case "video": return "bg-red-500";
      case "document": return "bg-blue-500";
      case "code": return "bg-green-500";
      case "archive": return "bg-yellow-500";
      case "email": return "bg-orange-500";
      default: return "bg-gray-500";
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case "Drive": return "bg-blue-900/50 text-blue-300";
      case "Notion": return "bg-gray-700/50 text-gray-300";
      case "Gmail": return "bg-red-900/50 text-red-300";
      case "Local": return "bg-green-900/50 text-green-300";
      default: return "bg-gray-700/50 text-gray-300";
    }
  };

  const handleDocumentSelect = async (document: Document) => {
    setSelectedDocument(document);
    // Track document access
    searchService.trackDocumentAccess(document);

    try {
      // Open emails with full preview + reply in sidebar
      if (document.type === 'email') {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
        const accountEmail = document.metadata?.accountEmail || '';
        const res = await fetch(`${baseUrl}/api/documents/email/${document.id}${accountEmail ? `?accountEmail=${encodeURIComponent(accountEmail)}` : ''}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const em = data.email || {};
          const email: EmailData = {
            id: em.id || document.id,
            threadId: em.threadId || document.metadata?.threadId,
            subject: em.subject || document.title,
            from: em.from || em.sender || '',
            to: em.to || '',
            date: em.date || em.parsedDate || new Date().toISOString(),
            parsedDate: em.parsedDate,
            snippet: em.snippet || '',
            fullBody: em.body || em.fullBody || '',
            labels: em.labels,
            isRead: em.isRead,
            hasAttachments: Array.isArray(em.attachments) ? em.attachments.length > 0 : false,
            attachments: em.attachments || [],
            sizeEstimate: em.sizeEstimate,
            accountEmail: accountEmail || em.accountEmail,
          };
          const evt = new CustomEvent('open-email-preview', { detail: { email } });
          window.dispatchEvent(evt);
          return;
        }
      }

      // For Drive files, fetch a richer preview
      if ((document.source === 'Drive' || document.metadata?.fileId) && document.metadata?.fileId) {
        const preview = await documentService.getDocumentPreview(document.metadata.fileId);
        const enriched = {
          ...document,
          name: document.title,
          preview: preview?.previewContent?.preview || document.preview,
          url: preview?.url || document.url,
        };
        const evt = new CustomEvent('open-document-preview', { detail: { document: enriched } });
        window.dispatchEvent(evt);
        return;
      }

      // Default: open whatever we have
      const evt = new CustomEvent('open-document-preview', { detail: { document: { ...document, name: document.title } } });
      window.dispatchEvent(evt);
    } catch (e) {
      console.warn('Failed to prepare preview:', e);
      const evt = new CustomEvent('open-document-preview', { detail: { document: { ...document, name: document.title } } });
      window.dispatchEvent(evt);
    }
  };

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      let results;
      
      if (searchQuery.trim()) {
        // Perform search across all sources
        const enabledSources = [];
        if (mcpServices.drive !== false) enabledSources.push('drive');
        if (mcpServices.email !== false) enabledSources.push('email');
        if (mcpServices.memory !== false) enabledSources.push('memory');
        enabledSources.push('recent'); // Always include recent documents

        console.log('Searching with sources:', enabledSources, 'query:', searchQuery);
        results = await searchService.searchAll(searchQuery, {
          sources: enabledSources as any,
          limit: 100
        });
        
        console.log('Search results:', results);
        setSearchResults(results);
        setDocuments(results.results);
      } else {
        // When no search query, try to load recent documents first, but don't fail if none exist
        console.log('Loading recent documents...');
        try {
          const recentDocs = await searchService.getRecentDocuments(50);
          console.log('Recent documents loaded:', recentDocs.length);
          setDocuments(recentDocs);
        } catch (recentError) {
          console.log('No recent documents found, that\'s normal for new users:', recentError);
          // If no recent documents, try to get some sample data from Drive/Email to show
          if (mcpServices.drive !== false) {
            console.log('Trying to load some Drive files...');
            try {
              const driveResults = await searchService.searchAll('', {
                sources: ['drive'],
                limit: 20
              });
              console.log('Drive files found:', driveResults.results.length);
              if (driveResults.results.length > 0) {
                setDocuments(driveResults.results);
                setSearchResults(driveResults);
              } else {
                // Show sample documents to demonstrate the UI is working
                setDocuments(getSampleDocuments());
              }
            } catch (driveError) {
              console.log('Drive files not available:', driveError);
              // Show sample documents to demonstrate the UI is working
              setDocuments(getSampleDocuments());
            }
          } else {
            // Show sample documents to demonstrate the UI is working
            setDocuments(getSampleDocuments());
          }
        }
        setSearchResults(null);
      }
    } catch (error) {
      console.error("Error loading documents:", error);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadDocuments();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, mcpServices]);

  return (
    <div className="h-full bg-slate-900 flex flex-col overflow-hidden">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-slate-800/60">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Search className="text-slate-400" size={18} />
                    <input
              placeholder="Search documents, emails, drive files..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent outline-none text-slate-200 placeholder-slate-500 text-sm py-2"
                    />
                  <button 
                    onClick={loadDocuments}
              className="px-3 py-2 rounded-md text-xs bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 flex items-center gap-2"
                  >
              <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>
          </div>
        </div>

        {/* Filter Tabs and Controls */}
        <div className="px-4 py-2 border-b border-slate-800/60">
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            {/* Filter Tabs */}
            <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-md p-1">
              {filterOptions.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-3 py-1 rounded-md text-xs transition-all duration-200 ${
                    activeFilter === filter.id
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                  }`}
                >
                  {filter.label}
                  <span className="ml-1 opacity-60">({filter.count})</span>
                </button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "grid" 
                    ? "bg-blue-600 text-white" 
                    : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white border border-slate-800"
                }`}
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "list" 
                    ? "bg-blue-600 text-white" 
                    : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white border border-slate-800"
                }`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Document Grid/List */}
        <div className="flex-1 overflow-auto p-4">
          <div className="max-w-5xl mx-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-24">
                <div className="flex flex-col items-center gap-4">
                  <RefreshCw className="animate-spin text-blue-400" size={48} />
                  <p className="text-slate-400">Loading documents...</p>
                </div>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-24 h-24 bg-slate-900 rounded-2xl flex items-center justify-center mb-4 border border-slate-800">
                  <FileText className="text-slate-400" size={48} />
                </div>
                <h3 className="text-base text-slate-200 mb-1">
                  {searchQuery ? "No search results" : "Connect Your Services"}
                </h3>
                <p className="text-slate-400 text-center max-w-md text-sm">
                  {searchQuery 
                    ? "Try adjusting your search query or browse through different categories" 
                    : "Go to Settings to connect Google Drive, Gmail, and other services to search your documents"
                  }
                </p>
                {!searchQuery && (
                  <button
                    onClick={onNavigateToSettings}
                    className="mt-3 px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-all duration-200"
                  >
                    Go to Settings
                  </button>
                )}
              </div>
            ) : viewMode === "list" ? (
              <div className="space-y-3">
                {filteredDocuments.map((document) => (
                  <motion.div
                    key={document.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group bg-slate-900 border border-slate-800 rounded-lg p-3 hover:bg-slate-800 hover:border-slate-700 transition-all duration-150 cursor-pointer"
                    onClick={() => handleDocumentSelect(document)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 ${getTypeColor(document.type)} rounded-md flex items-center justify-center flex-shrink-0`}>
                        {document.thumbnail ? (
                          <span className="text-xl">{document.thumbnail}</span>
                        ) : (
                          getIcon(document.type)
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-slate-200 font-medium truncate text-sm">{document.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-[10px] ${getSourceColor(document.source)}`}>
                            {document.source}
                          </span>
                        </div>
                        <p className="text-slate-400 text-xs line-clamp-2">{document.description}</p>
                        {document.preview && (
                          <p className="text-slate-500 text-[11px] line-clamp-1 mt-1 font-mono">{document.preview.substring(0,80)}{document.preview.length>80?'…':''}</p>
                        )}
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1.5">
                          <span>{document.size}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {document.modified}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button className="bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md border border-slate-700">
                          <MessageSquare size={16} />
                        </button>
                        <button className="bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md border border-slate-700">
                          <Eye size={16} />
                        </button>
                        <button className="bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md border border-slate-700">
                          <Download size={16} />
                        </button>
                        <button className="bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md border border-slate-700">
                          <MoreHorizontal size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredDocuments.map((document, index) => (
                  <motion.div
                    key={document.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="group relative bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:bg-slate-800 hover:border-slate-700 transition-all duration-200 cursor-pointer"
                    onClick={() => handleDocumentSelect(document)}
                  >
                    {/* Preview Image or Icon */}
                    <div className="aspect-[4/3] relative overflow-hidden">
                      {document.preview ? (
                        <img
                          src={document.preview}
                          alt={document.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`w-full h-full ${getTypeColor(document.type)} flex items-center justify-center`}>
                          {document.thumbnail ? (
                            <span className="text-4xl">{document.thumbnail}</span>
                          ) : (
                            getIcon(document.type)
                          )}
                        </div>
                      )}
                      
                      {/* Source Badge */}
                      <div className="absolute top-2 right-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${getSourceColor(document.source)} backdrop-blur-sm`}>
                          {document.source}
                        </span>
                      </div>

                      {/* Hover Actions */}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-150">
                        <button className="bg-slate-900/90 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md backdrop-blur-sm border border-slate-700 transition-all duration-150">
                          <MessageSquare size={16} />
                        </button>
                        <button className="bg-slate-900/90 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md backdrop-blur-sm border border-slate-700 transition-all duration-150">
                          <Eye size={16} />
                        </button>
                        <button className="bg-slate-900/90 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-md backdrop-blur-sm border border-slate-700 transition-all duration-150">
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="p-4">
                      <h3 className="text-slate-200 font-medium truncate text-sm mb-1">{document.title}</h3>
                      <p className="text-slate-400 text-xs line-clamp-2 mb-2">{document.description}</p>
                      {document.preview && (
                        <p className="text-slate-500 text-[11px] line-clamp-1 mb-2 font-mono">{document.preview.substring(0,80)}{document.preview.length>80?'…':''}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span>{document.size}</span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {document.modified}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 